import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Stack,
  TablePagination,
  TextField,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LicenseAlert } from '../types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

interface AlertsPanelProps {
  alerts: LicenseAlert[];
  loading?: boolean;
}

function getSeverityColor(severity: LicenseAlert['severity']) {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'info';
  }
}

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'daysRemaining' | 'expiresAt';

export default function AlertsPanel({ alerts, loading }: AlertsPanelProps) {
  const { t } = useTranslation();

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter State
  const [searchText, setSearchText] = useState('');

  // Sort State
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('daysRemaining');

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredAlerts = useMemo(() => {
    let result = [...alerts];

    // Filter
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(lower) ||
          a.dealerName.toLowerCase().includes(lower) ||
          a.code.toLowerCase().includes(lower)
      );
    }

    // Sort
    result.sort((a, b) => {
      let valA: any = a[orderBy];
      let valB: any = b[orderBy];

      if (orderBy === 'name') {
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [alerts, searchText, order, orderBy]);

  // Paginate
  const visibleAlerts = useMemo(() => {
    return filteredAlerts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAlerts, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Card sx={{ minWidth: 200 }}>
        <CardContent>
          <Typography color="text.secondary">{t('common.loading', 'Loading...')}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
                <Typography variant="h6" fontWeight={600}>
                {t('dashboard.alerts.title', 'Expiring Licenses')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                {t('dashboard.alerts.subtitle', 'Tenants with licenses expiring in the next 30 days')}
                </Typography>
            </Box>

            <TextField
                size="small"
                placeholder={t('common.search', 'Search...')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ minWidth: 200 }}
            />
          </Box>

          {alerts.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography color="text.secondary" fontWeight={500}>
                {t('dashboard.alerts.noExpiring', 'No licenses expiring in the next 30 days!')}
              </Typography>
            </Box>
          ) : (
            <>
                <TableContainer>
                <Table size="small">
                    <TableHead>
                    <TableRow>
                        <TableCell sortDirection={orderBy === 'name' ? order : false}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={() => handleRequestSort('name')}
                            >
                                {t('dashboard.alerts.tenant', 'Tenant')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>{t('dashboard.alerts.dealer', 'Dealer')}</TableCell>
                        <TableCell sortDirection={orderBy === 'expiresAt' ? order : false}>
                             <TableSortLabel
                                active={orderBy === 'expiresAt'}
                                direction={orderBy === 'expiresAt' ? order : 'asc'}
                                onClick={() => handleRequestSort('expiresAt')}
                            >
                                {t('dashboard.alerts.expiresAt', 'Expires')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={orderBy === 'daysRemaining' ? order : false}>
                             <TableSortLabel
                                active={orderBy === 'daysRemaining'}
                                direction={orderBy === 'daysRemaining' ? order : 'asc'}
                                onClick={() => handleRequestSort('daysRemaining')}
                            >
                                {t('dashboard.alerts.daysRemaining', 'Days')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>{t('dashboard.alerts.status', 'Status')}</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {visibleAlerts.map((alert) => (
                        <TableRow key={alert.id} hover>
                        <TableCell>
                            <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {alert.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {alert.code}
                            </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>{alert.dealerName}</TableCell>
                        <TableCell>
                            {new Date(alert.expiresAt).toLocaleDateString(t('meta.languageCode', undefined))}
                        </TableCell>
                        <TableCell align="right">
                            <Typography
                            fontWeight={700}
                            color={
                                alert.daysRemaining <= 7
                                ? 'error.main'
                                : alert.daysRemaining <= 14
                                    ? 'warning.main'
                                    : 'text.primary'
                            }
                            >
                            {alert.daysRemaining}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                            label={
                                alert.isDemo
                                ? t('dashboard.alerts.trial', 'Trial')
                                : alert.severity === 'critical'
                                    ? t('dashboard.alerts.critical', 'Critical')
                                    : alert.severity === 'warning'
                                    ? t('dashboard.alerts.warning', 'Warning')
                                    : t('dashboard.alerts.active', 'Active')
                            }
                            color={getSeverityColor(alert.severity)}
                            size="small"
                            variant={alert.isDemo ? 'outlined' : 'filled'}
                            />
                        </TableCell>
                        </TableRow>
                    ))}
                    {visibleAlerts.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    {t('common.noResults', 'No results found')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredAlerts.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('common.rowsPerPage', 'Rows per page:')}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of', 'of')} ${count !== -1 ? count : `more than ${to}`}`}
                />
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
